/**
 * Work authorizations — the gate on anything outside the subscription.
 *
 * Nothing becomes separately billable without an approved row here. The
 * database enforces it: biz_svc_time_entries carries a CHECK that refuses
 * billable_class = 'billable' without an authorization_id, and the procedure
 * refuses an authorization that is not approved.
 *
 * So this screen is not a convenience — it is the only path by which extra work
 * can ever be charged.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Authorization } from '@/lib/api';
import { useAuth } from '@/features/auth/auth-context';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Check, X, Plus } from 'lucide-react';

export function AuthorizationsPanel({ engagementId, cycleId }: {
  engagementId: string;
  cycleId?: string;
}) {
  const { user } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  const qc = useQueryClient();
  const { toast } = useToast();

  const [requesting, setRequesting] = useState(false);
  const [description, setDescription] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [quotedAmount, setQuotedAmount] = useState('');
  const [billingRate, setBillingRate] = useState('');

  const { data } = useQuery({
    queryKey: ['svc', 'authorizations', engagementId],
    queryFn: () => api.getAuthorizations({ engagementId }),
    // The list endpoint is manager-gated; a restricted user can request but
    // not browse.
    enabled: isManager,
    retry: false,
  });
  const rows = (data?.data ?? []) as Authorization[];

  const request = useMutation({
    mutationFn: () =>
      api.createAuthorization({
        engagementId,
        cycleId: cycleId ?? null,
        description,
        estimatedHours: estimatedHours === '' ? null : Number(estimatedHours),
        quotedAmount: quotedAmount === '' ? null : Number(quotedAmount),
        billingRate: billingRate === '' ? null : Number(billingRate),
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not request', description: r.error?.message, variant: 'destructive' });
        return;
      }
      setDescription(''); setEstimatedHours(''); setQuotedAmount(''); setBillingRate('');
      setRequesting(false);
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Authorization requested', description: 'It is inert until approved.' });
    },
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api.decideAuthorization(id, { decision }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not record', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Recorded' });
    },
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Extra work</CardTitle>
          {!requesting && (
            <Button size="sm" variant="outline" onClick={() => setRequesting(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Request authorization
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Work outside the subscription. Hours against an approved authorization are the only
          ones that can ever be billed separately — logging time is not the same as charging
          for it.
        </p>

        {requesting && (
          <div className="space-y-3 rounded-md border p-3">
            <div>
              <Label className="text-xs">What is being asked for?</Label>
              <Textarea
                className="mt-1" rows={2} value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Build an additional service page for heavy-duty recovery"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Estimated hours</Label>
                <Input
                  className="mt-1" type="number" min={0} step="0.25"
                  value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Quoted amount</Label>
                <Input
                  className="mt-1" type="number" min={0} step="0.01"
                  value={quotedAmount} onChange={(e) => setQuotedAmount(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Hourly rate</Label>
                <Input
                  className="mt-1" type="number" min={0} step="0.01"
                  value={billingRate} onChange={(e) => setBillingRate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm" onClick={() => request.mutate()}
                disabled={!description || request.isPending}
              >
                Request
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRequesting(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {!isManager ? (
          <p className="text-xs text-muted-foreground">
            Your requests go to your manager for approval.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No extra work requested.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {rows.map((a) => (
              <li key={a.id} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm">{a.description}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(a.requested_at)}
                      {a.requested_by_name ? ` · ${a.requested_by_name}` : ''}
                      {a.estimated_hours != null ? ` · est ${a.estimated_hours}h` : ''}
                      {a.quoted_amount != null ? ` · ${formatCurrency(a.quoted_amount)}` : ''}
                      {' · '}{Number(a.hours_logged ?? 0).toFixed(2)}h logged
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={
                      a.status === 'approved' ? 'default'
                      : a.status === 'declined' ? 'destructive' : 'outline'
                    }>
                      {a.status}
                    </Badge>
                    {a.status === 'requested' && (
                      <>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => decide.mutate({ id: a.id, decision: 'approved' })}
                          disabled={decide.isPending}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => decide.mutate({ id: a.id, decision: 'declined' })}
                          disabled={decide.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
